import queryString from 'query-string';
import { push } from 'react-router-redux';

import {
  ADD_TO_CHECKOUT_REFERRER_HISTORY,
  CHECKOUT_CONFIGURE_IS_PROCESSING,
  CHECKOUT_DEFAULT_SELECTED_SHIP_OPTION,
  CHECKOUT_IS_LOADED,
  CHECKOUT_ORDER_BEING_PLACED,
  CHECKOUT_RESET_STATE,
  CHECKOUT_RESET_TO_DEFAULT_STATE,
  CHECKOUT_SET_CONFIRM_CARD_ERROR_MSG,
  CHECKOUT_SET_CONFIRMATION_PIXEL_PAYLOAD,
  CHECKOUT_SET_SHOULD_DEFAULT_OPTIONS,
  CHECKOUT_STORE_LOADING_REVIEW_SECTION,
  CHECKOUT_STORE_NO_PROMO_CODE_WARNING,
  CHECKOUT_TRACK_NEW_CUSTOMER,
  CHECKOUT_UPDATE_PURCHASE_CC,
  CHECKOUT_UPDATED_QUANTITY,
  INITIALIZE_SAVED_ADDRESSES_AND_PAYMENTS_FETCHED,
  RECEIVE_CHECKOUT_DATA,
  RECEIVE_FIELD_VALIDATION_ERRORS,
  RECEIVE_ORDER_INFORMATION,
  REDIRECT,
  REMOVE_FROM_CHECKOUT_REFERRER_HISTORY,
  SET_CHECKOUT_MAX_AVAILABLE_STEP,
  SET_IS_ALSO_BILLING,
  SET_SHOULD_DEFAULT_OPTIONS,
  SET_USE_NAME_ON_ACCOUNT,
  TRACK_HAS_SEEN_IS_ALSO_BILLING,
  TRACK_HAS_TRACKED_PAGE_VIEW,
  UPDATE_CHECKOUT_FOR_TARGET_STEP,
  USE_PROMO_BALANCE
} from 'constants/reduxActions';
import { CHECKOUT_STEP_MAP, REVIEW_STEP, TBD_STEP } from 'constants/checkoutFlow';
import { SHIP_OPTION_GROUP_RETAIL_W_OPTIONS } from 'constants/shipOptionGroups';
import { pvOrderConfirmation } from 'events/checkout';

import { extraStepsForAddressDelete, receiveCheckoutConfigureAddresses } from 'actions/checkout/addresses';
import { conditionallyCollapseGiftCardModal } from 'actions/checkout/giftCards';
import { breakShipOptionsOutByGroup, receiveCheckoutShipOptions, storeEGCOnlyShipOptions } from 'actions/checkout/shipOptions';
import { defaultSelectedPayment, receiveCheckoutConfigurePayments } from 'actions/checkout/payments';
import { err, setError } from 'actions/errors';
import { setSessionCookies } from 'actions/session';
import { track } from 'apis/amethyst';
import { checkoutData, getOrdersByPurchaseIdV2, getPixelData, placeOrder, shipOptionsV2 } from 'apis/mafia';
import marketplace from 'cfg/marketplace.json';
import { trackOrderConfirmationEvent } from 'helpers/analytics';
import { isDesktop } from 'helpers/ClientUtils';
import {
  ERROR_CANNOT_CONFIRM_PURCHASE_OOS,
  ERROR_CANNOT_CONFIRM_PURCHASE_OTHER,
  ERROR_EMPTY_CART,
  ERROR_INVALID_GIFT_OPTIONS,
  ERROR_NOT_AUTHENTICATED,
  ERROR_PURCHASE_NOT_FOUND,
  ERROR_QUANTITY_CHANGE_REQUEST_VALIDATION,
  ERROR_REQUEST_VALIDATION,
  fetchApiNotAuthenticatedMiddleware,
  fetchCannotConfirmPurchaseMiddleware,
  fetchCheckout400ErrorMiddleware,
  fetchErrorMiddleware,
  fetchPixelErrorMiddleware,
  fetchPurchaseNotFoundErrorMiddleware
} from 'middleware/fetchErrorMiddleware';
import logger from 'middleware/logger';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { buildAuthenticationRedirectUrl } from 'utils/redirect';
import { CHECKOUT_API_ERROR_NOT_AUTHORIZED } from 'store/ducks/checkout/types';

const { subsiteId: { desktop: desktopSubsiteId, mobile: mobileSubsiteId } } = marketplace;

export const catchHandlerFactory = (dispatch, redirectPath = CHECKOUT_STEP_MAP[TBD_STEP]) => e => {
  dispatch(setCheckoutConfigureIsProcessing(false));

  switch (e.id) {
    case ERROR_CANNOT_CONFIRM_PURCHASE_OOS: {
      const { asin } = e.extraInformation[0];
      dispatch(alertItemUnavailableForPurchase(asin));
      dispatch(resetToDefaultState());
      dispatch(fetchCheckoutData({ initializeAddressesAndPayments: true, includeShipmentOptions: true }));
      return;
    }
    case ERROR_CANNOT_CONFIRM_PURCHASE_OTHER: {
      alert('Sorry, an error has occurred.  Please try placing your order again.');
      dispatch(resetToDefaultState());
      dispatch(fetchCheckoutData({ initializeAddressesAndPayments: true, includeShipmentOptions: true }));
      return;
    }
    case ERROR_INVALID_GIFT_OPTIONS: {
      const alertMessage = e.extraInformation === 'GiftMessage is required' ? 'Please provide a gift message.' : e.extraInformation;
      alert(alertMessage);
      return;
    }
    case ERROR_PURCHASE_NOT_FOUND: {
      dispatch(resetToDefaultState());
      dispatch(fetchCheckoutData({ initializeAddressesAndPayments: true, includeShipmentOptions: true }));
      return dispatch({ type: REDIRECT, location: CHECKOUT_STEP_MAP[TBD_STEP] });
    }
    case ERROR_QUANTITY_CHANGE_REQUEST_VALIDATION: {
      alert(e.extraInformation);
      return;
    }
    case ERROR_REQUEST_VALIDATION:
      return dispatch(addFieldValidationErrors(e.extraInformation));
    case ERROR_NOT_AUTHENTICATED:
      dispatch({ type: CHECKOUT_API_ERROR_NOT_AUTHORIZED });
      return dispatch({ type: REDIRECT, location: buildAuthenticationRedirectUrl(redirectPath) });
    case ERROR_EMPTY_CART:
      return dispatch({ type: REDIRECT, location: '/cart' });
    default: {
      return dispatch(setError(err.GENERIC, e));
    }
  }
};

export function defaultEndpoints() {
  return { checkoutData, getOrdersByPurchaseIdV2, placeOrder, shipOptions: shipOptionsV2, getPixelData };
}

export function fetchCheckoutData(info = {}, mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const { checkoutData, cookies, environmentConfig: { api: { mafia } } } = getState();
    const { purchaseData: { data, usePromoBalance } } = checkoutData;
    const { shippingAddressId, purchaseId: purchasePurchaseId } = data;

    const {
      addressId,
      advanceOnSuccess,
      advanceOnSuccessAsRedirect,
      callback = f => f,
      coupon,
      extraStepsOnDeleteParams,
      giftOptions,
      hasInvalidAddress,
      includePaymentsAndAddresses,
      includeShipmentOptions: includeShipmentOptionsFromInfo,
      initializeAddressesAndPayments,
      paymentMethods,
      purchaseId,
      quantityUpdate,
      shipmentOptionId,
      showFullPageSpinner = true
    } = info;

    if (showFullPageSpinner) {
      dispatch({ type: CHECKOUT_IS_LOADED, isLoaded: false });
    } else {
      dispatch(setCheckoutConfigureIsProcessing(true));
    }

    const applyPromoBalances = typeof info.usePromoBalance !== 'undefined' ? info.usePromoBalance : !!usePromoBalance;

    // use what the user has passed in, otherwise mafia will use what is already on the purchase
    const reqData = {
      useGCBalance: applyPromoBalances,
      useDiscount: applyPromoBalances,
      purchaseId: purchasePurchaseId
    };

    let includeAssociated = false,
      includePaymentsDueToAssociation = false,
      includeShipmentOptions = false;

    if (includeShipmentOptionsFromInfo) {
      includeShipmentOptions = true;
    }

    if (quantityUpdate) {
      reqData.quantityUpdate = quantityUpdate;
    }

    if (coupon) {
      reqData.coupon = coupon;
    }

    if (purchaseId) {
      reqData.purchaseId = purchaseId;
    }

    if (addressId) {
      reqData.addressId = addressId;
      includeShipmentOptions = true;

      // if we have an address that isn't already our ship address,
      // we need to look up associations
      if (addressId !== shippingAddressId) {
        includePaymentsDueToAssociation = true;
        includeAssociated = true;
      }
    }

    // on initial load, grab associated addresses for our payments in event account has a default ship address
    if (initializeAddressesAndPayments) {
      includeAssociated = true;
    }

    if (paymentMethods) {
      reqData.paymentMethods = paymentMethods;
    }

    if (shipmentOptionId) {
      reqData.shipmentOptionId = shipmentOptionId;
      // lineItemId could change under us, so we need to refresh the delivery options
      // since those groups are based around lineItemId - else the entire group
      // will fail to display in the review section :(
      // ticket to optimize in future: https://github01.zappos.net/mweb/marty/issues/4263
      includeShipmentOptions = true;
    }

    if (giftOptions) {
      reqData.giftOptions = giftOptions;
    }

    const params = {
      url: mafia.url,
      reqData,
      credentials: cookies,
      includeAssociated,
      includePaymentsAndAddresses: includePaymentsDueToAssociation || initializeAddressesAndPayments || includePaymentsAndAddresses,
      includeShipmentOptions
    };

    return mafiaEndpoints.checkoutData(params)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchPurchaseNotFoundErrorMiddleware)
      .then(fetchCheckout400ErrorMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        // !!! order is important !!!
        dispatch(receiveCheckoutShipOptions(response));
        dispatch(receiveCheckoutData(response));
        dispatch(receiveCheckoutConfigureAddresses(response, !!hasInvalidAddress));
        dispatch(breakShipOptionsOutByGroup());
        dispatch(storeEGCOnlyShipOptions());
        dispatch(receiveCheckoutConfigurePayments(response));
        dispatch(updatePurchaseCreditCard());
        dispatch(defaultSelectedPayment());
        dispatch(defaultSelectedShipOption());
        dispatch(setShowShouldDefaultOptions());
        dispatch(conditionallyCollapseGiftCardModal());
        dispatch(storeIsNewCustomer());

        if (initializeAddressesAndPayments) {
          dispatch({ type: INITIALIZE_SAVED_ADDRESSES_AND_PAYMENTS_FETCHED });
        }

        if (extraStepsOnDeleteParams) {
          dispatch(extraStepsForAddressDelete(extraStepsOnDeleteParams));
        }

        if (advanceOnSuccess) {
          dispatch(updateCheckoutForTargetStep({ asRedirect: advanceOnSuccessAsRedirect, targetStep: REVIEW_STEP, newUrl: CHECKOUT_STEP_MAP[REVIEW_STEP] }));
        }

        if (quantityUpdate) {
          dispatch(fireQuantityEvent());
        }

        callback();
      })
      .catch(catchHandlerFactory(dispatch));
  };
}

export function redirectUrl(location) {
  return { type: REDIRECT, location };
}

export function pushUrl(location) {
  return function (dispatch) {
    dispatch(push(location));
  };
}

export function receiveCheckoutData(data) {
  return { type: RECEIVE_CHECKOUT_DATA, data };
}

export function setCheckoutMaxAvailableStep(maxAvailableStep) {
  return { type: SET_CHECKOUT_MAX_AVAILABLE_STEP, maxAvailableStep };
}

export function setUsePromoBalance(usePromoBalance) {
  return { type: USE_PROMO_BALANCE, usePromoBalance };
}

export function advanceCheckout() {
  return function (dispatch, getState) {
    const {
      checkoutData: {
        purchaseData: { data: { purchaseId } }
      }
    } = getState();

    return dispatch(pushUrl(`${CHECKOUT_STEP_MAP[REVIEW_STEP]}?pid=${purchaseId}`));
  };
}

export function setIsLoadingReviewSection(isLoadingReviewSection) {
  return { type: CHECKOUT_STORE_LOADING_REVIEW_SECTION, isLoadingReviewSection };
}

export function setIsAlsoBilling(isAlsoBilling) {
  return { type: SET_IS_ALSO_BILLING, isAlsoBilling };
}

export function setUseNameOnAccount(useNameOnAccount) {
  return { type: SET_USE_NAME_ON_ACCOUNT, useNameOnAccount };
}

export function addFieldValidationErrors(requestExtraInformation) {
  return { type: RECEIVE_FIELD_VALIDATION_ERRORS, requestExtraInformation };
}

export function trackHasTrackedPageView() {
  return { type: TRACK_HAS_TRACKED_PAGE_VIEW };
}

export function trackHasSeenIsAlsoBilling() {
  return { type: TRACK_HAS_SEEN_IS_ALSO_BILLING };
}

export function setCheckoutConfigureIsProcessing(isFetchingCheckoutData) {
  return { type: CHECKOUT_CONFIGURE_IS_PROCESSING, isFetchingCheckoutData };
}

export function handleInvalidCheckoutPath(path) {
  return function (dispatch, getState) {
    const {
      checkoutData: { purchaseData: { data: { purchaseId } } },
      routing: { locationBeforeTransitions: { search } }
    } = getState();

    const { pid } = queryString.parse(search);
    const qsWhenNoPid = search ? `${search}&pid=${purchaseId}` : `?pid=${purchaseId}`;

    const qsWithPurchaseId = pid
      ? search
      : qsWhenNoPid;

    dispatch(redirectUrl(`${path}${qsWithPurchaseId}`));
  };
}

export function updateCheckoutForTargetStep(params) {
  const {
    asRedirect = false,
    newUrl,
    preserveQueryString = false,
    suppliedQs,
    targetStep
  } = params;

  return function (dispatch, getState) {
    const {
      checkoutData: { purchaseData: { data: { purchaseId } } },
      routing: { locationBeforeTransitions: { search } }
    } = getState();

    const { editDestinationId } = queryString.parse(search);
    const qsWithPurchaseId = `?pid=${purchaseId}`;
    let decoratedQueryString = editDestinationId && preserveQueryString
      ? `${qsWithPurchaseId}&editDestinationId=${editDestinationId}`
      : qsWithPurchaseId;

    decoratedQueryString = suppliedQs
      ? `${decoratedQueryString}&${suppliedQs}`
      : decoratedQueryString;

    dispatch({ type: CHECKOUT_RESET_STATE });
    dispatch({ type: UPDATE_CHECKOUT_FOR_TARGET_STEP, targetStep });
    if (asRedirect) {
      dispatch(redirectUrl(`${newUrl}${decoratedQueryString}`));
    } else {
      dispatch(pushUrl(`${newUrl}${decoratedQueryString}`));
    }
  };
}

export function updatePurchaseCreditCard() {
  return function (dispatch, getState) {
    const {
      checkoutData: {
        purchaseData: { purchaseCreditCard },
        paymentTypes: { saved: savedPayments }
      }
    } = getState();

    if (!savedPayments || !purchaseCreditCard) {
      return;
    }

    const savedCreditCard = (savedPayments || []).find(
      item => item.paymentInstrumentId === purchaseCreditCard.paymentInstrumentId);

    const updatedPurchaseCreditCard = { ...purchaseCreditCard, ...savedCreditCard };

    dispatch({ type: CHECKOUT_UPDATE_PURCHASE_CC, purchaseCreditCard: updatedPurchaseCreditCard });
  };
}

export function defaultSelectedShipOption() {
  return function (dispatch, getState) {
    const {
      checkoutData: {
        purchaseData: {
          data: { promise }
        },
        shipOptions: { groups = {} }
      }
    } = getState();

    const retailShipOptions = groups[SHIP_OPTION_GROUP_RETAIL_W_OPTIONS] || { deliveryOptions: [] };
    const savedShipOptions = retailShipOptions.deliveryOptions;

    if (!promise || !savedShipOptions) {
      return;
    }

    const shipOption = (savedShipOptions || []).find(
      item => item.promise === promise) || {};

    dispatch({ type: CHECKOUT_DEFAULT_SELECTED_SHIP_OPTION, purchaseShipOptionId: shipOption.id });
  };
}

export function hasFieldErrors(errorMap) {
  if (!errorMap || !Object.keys(errorMap).length) {
    return;
  }
  return Object.keys(errorMap).reduce((acc, key) => (!!((errorMap[key] && errorMap[key].length) || acc)), false);
}

export function pushPlaceOrder(mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const { checkoutData, environmentConfig: { api: { mafia } } } = getState();
    const {
      purchaseData,
      purchaseData: {
        data: { chargeSummary, productList, purchaseId, versionNumber },
        isOrderBeingPlaced,
        shouldDefaultOptions
      }
    } = checkoutData;

    if (isOrderBeingPlaced) {
      return;
    }

    dispatch(setOrderBeingPlaced(true));

    return mafiaEndpoints.placeOrder(mafia, purchaseId, versionNumber, shouldDefaultOptions)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(fetchCannotConfirmPurchaseMiddleware)
      .then(({ orderId, digitalOrderId }) => {
        const orderConfirmationEventData = buildOrderConfirmationEventData({ chargeSummary, digitalOrderId, orderId, productList, purchaseId });
        trackOrderConfirmationEvent(orderConfirmationEventData);
        track(pvOrderConfirmation, { purchaseData, digitalOrderId, orderId });
        dispatch(setOrderBeingPlaced(false));
        dispatch(pushToThankYouPage(purchaseId));
      })
      .catch(catchHandlerFactory(dispatch));
  };
}

export function setOrderBeingPlaced(isOrderBeingPlaced) {
  return { type: CHECKOUT_ORDER_BEING_PLACED, isOrderBeingPlaced };
}

export function pushToThankYouPage(orderId) {
  return { type: REDIRECT, location: `/confirmation/${orderId}` };
}

export function setShouldDefaultOptions(shouldDefaultOptions) {
  return { type: SET_SHOULD_DEFAULT_OPTIONS, shouldDefaultOptions };
}

export function setShowShouldDefaultOptions() {
  return function (dispatch, getState) {
    const { checkoutData } = getState();
    const {
      addresses: { saved: savedAddresses },
      purchaseData: {
        data: { shippingAddressId },
        doesPurchaseRequireCC,
        purchaseCreditCard,
        usePromoBalance
      }
    } = checkoutData;

    const paymentMethodIsNotPrimary = purchaseCreditCard && !purchaseCreditCard.primaryPaymentMethod;
    const address = (savedAddresses || []).find(
      item => item.addressId === shippingAddressId);
    const addressIsNotPrimray = address && !address.isDefaultShippingAddress;
    const showShouldDefaultOptions = (!usePromoBalance || doesPurchaseRequireCC) && (paymentMethodIsNotPrimary || addressIsNotPrimray);

    dispatch({ type: CHECKOUT_SET_SHOULD_DEFAULT_OPTIONS, showShouldDefaultOptions: !!showShouldDefaultOptions, shouldDefaultOptions: !!showShouldDefaultOptions });
  };
}

export function fetchConfirmationPixelPayload(pId, endpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();

    if (!pId) {
      return;
    }

    return endpoints.getPixelData(mafia, pId, cookies)
      .then(fetchPixelErrorMiddleware)
      .then(confirmationPixelPayload => {
        if (confirmationPixelPayload) {
          dispatch({ type: CHECKOUT_SET_CONFIRMATION_PIXEL_PAYLOAD, confirmationPixelPayload });
        }
      })
      .catch(() => {
        logger('Unable to get valid response from /pixel endpoint, not firing pixel server. This is expected in non zfc environments.');
      });
  };
}

export function fetchOrdersByPurchaseId(pId, callback, mafiaEndpoints = defaultEndpoints()) {
  return function (dispatch, getState) {
    const { cookies, environmentConfig: { api: { mafia } } } = getState();

    if (!pId) {
      return;
    }

    return mafiaEndpoints.getOrdersByPurchaseIdV2(mafia, pId, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch)))
      .then(fetchApiNotAuthenticatedMiddleware)
      .then(fetchErrorMiddleware)
      .then(response => {
        if (callback) {
          callback(response, pId);
        }
        dispatch(receiveOrderInformation(response));
      })
      .catch(catchHandlerFactory(dispatch, `/confirmation/${pId}`));
  };
}

export function receiveOrderInformation(data) {
  return { type: RECEIVE_ORDER_INFORMATION, data };
}

export function addToCheckoutReferrerHistory(page) {
  return { type: ADD_TO_CHECKOUT_REFERRER_HISTORY, page };
}

export function removeFromCheckoutReferrerHistory(page) {
  return { type: REMOVE_FROM_CHECKOUT_REFERRER_HISTORY, page };
}

export function goToCart() {
  return { type: REDIRECT, location: '/cart' };
}

export function resetToDefaultState() {
  return { type: CHECKOUT_RESET_TO_DEFAULT_STATE };
}

export function storeNoPromoCodeWarning() {
  return { type: CHECKOUT_STORE_NO_PROMO_CODE_WARNING };
}

export function fireQuantityEvent() {
  return {
    type: CHECKOUT_UPDATED_QUANTITY
  };
}

export function storeIsNewCustomer() {
  return function (dispatch, getState) {
    const {
      checkoutData: {
        addresses: { saved: savedAddresses },
        paymentTypes: { saved: savedPaymentTypes },
        purchaseData: { isNewCustomer }
      }
    } = getState();

    // if flagged as new at any point in the purchase, don't update this again
    if (isNewCustomer) {
      return;
    }

    // if has saved payments or addreses, tread them as not being new
    const newCustomer = !(savedAddresses && savedAddresses.length || savedPaymentTypes && savedPaymentTypes.length);

    dispatch({ type: CHECKOUT_TRACK_NEW_CUSTOMER, isNewCustomer: newCustomer });
  };
}

export function setConfirmCardMessage(confirmCardMessage) {
  return { type: CHECKOUT_SET_CONFIRM_CARD_ERROR_MSG, confirmCardMessage };
}

export function alertItemUnavailableForPurchase(asin, alertUser = window.alert) {
  return function (dispatch, getState) {
    const { checkoutData: { purchaseData: { data: { productList = [] } } } } = getState();
    const item = productList.find(cv => cv.asin === asin);

    if (item) {
      const { productName } = item;
      alertUser(`Sorry, ${productName} is no longer available for purchase.`);
    }
  };
}

export function buildOrderConfirmationEventData(params) {
  const subsiteId = isDesktop(marketplace) ? desktopSubsiteId : mobileSubsiteId;

  const {
    orderId: amazonPhysicalOrderId,
    digitalOrderId: amazonDigitalOrderId,
    purchaseId: amazonPurchaseId,
    chargeSummary: { shippingCharge, estimatedTax, subTotal, total },
    productList
  } = params;

  const orderItem = productList.map(item => {
    const { productId, stockId, asin } = item;
    return { asin, productId, stockId };
  });

  const checkout = {
    amazonPurchaseId,
    amazonDigitalOrderId,
    amazonPhysicalOrderId,
    shipping: +shippingCharge.toFixed(2),
    tax: +estimatedTax.toFixed(2),
    subtotal: +subTotal.toFixed(2),
    total: +total.toFixed(2),
    orderItem
  };

  return {
    type: 'Checkout',
    checkout,
    subsiteId
  };
}
